import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ClipboardList, Compass } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ErrorState, EmptyState } from "@/components/shared";
import { cn } from "@/lib/utils";
import { usePortalData, appStatus, GROUPS } from "@/components/candidate_portal/portal";
import { AppRow } from "@/components/candidate_portal/AppRow";

const FILTERS = [["all", "All"], ["needs", "Needs you"], ["waiting", "In progress"], ["closed", "Closed"]];

export default function CandidateApplications() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [filter, setFilter] = useState("all");
  const d = usePortalData();
  const openId = params.get("open");
  const toggle = (id) => setParams(openId === id ? {} : { open: id }, { replace: true });

  const header = (
    <header className="flex flex-wrap items-end justify-between gap-4">
      <h1 className="text-3xl sm:text-4xl font-display font-semibold tracking-tight" data-testid="applications-title">Applications</h1>
      <Button variant="outline" onClick={() => nav("/candidate/jobs")} className="gap-2" data-testid="browse-roles"><Compass className="h-4 w-4" /> Browse roles</Button>
    </header>
  );
  const shell = (children) => <div className="max-w-[1120px] mx-auto px-4 sm:px-6 py-8 sm:py-10 space-y-6" data-testid="applications-page">{header}{children}</div>;

  if (d.apps === undefined) return shell(<div className="space-y-3" role="status" aria-busy="true">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-[72px] rounded-[14px]" />)}</div>);
  if (d.apps === null) return shell(<ErrorState message="We couldn't load your applications." onRetry={d.reload} />);
  if (!d.apps.length) return shell(<EmptyState icon={ClipboardList} title="No applications yet" description="Explore roles matched to your profile and apply in one tap." action={<Button className="mt-2" onClick={() => nav("/candidate/jobs")} data-testid="find-role">Find a role</Button>} />);

  const rows = d.apps.map((a) => ({ a, s: appStatus(a, d.interviews) }));
  const count = (k) => (k === "all" ? rows.length : rows.filter((r) => r.s.group === k).length);
  const groups = GROUPS.filter(([g]) => (filter === "all" || filter === g) && count(g) > 0);

  return shell(
    <>
      <div role="tablist" aria-label="Filter applications" className="flex flex-wrap gap-2">
        {FILTERS.map(([k, label]) => (
          <button key={k} role="tab" aria-selected={filter === k} data-active={filter === k} onClick={() => setFilter(k)} className="hs-pill" data-testid={`apps-filter-${k}`}>
            {label} <span className="tabular-nums opacity-70">{count(k)}</span>
          </button>
        ))}
      </div>
      {groups.length === 0 && <p className="text-sm text-muted-foreground py-6" data-testid="apps-filter-empty">Nothing here right now.</p>}
      {groups.map(([g, label, tone]) => (
        <section key={g} aria-labelledby={`apps-group-${g}`} className="space-y-2.5" data-testid={`apps-group-${g}`}>
          <h2 id={`apps-group-${g}`} className={cn("text-xs font-semibold uppercase tracking-[0.12em]", tone)}>{label}</h2>
          <ul className="space-y-2.5 hs-stagger">
            {rows.filter((r) => r.s.group === g).map(({ a, s }) => <AppRow key={a.id} a={a} s={s} open={openId === a.id} onToggle={() => toggle(a.id)} d={d} />)}
          </ul>
        </section>
      ))}
    </>
  );
}
